import React from "react";
import { useForm, useWatch } from "react-hook-form";
import { formatInTimeZone } from "date-fns-tz";
import {
  Button,
  Modal,
  Grid,
  Text,
  Divider,
  RadioController,
  DateController,
  NumberController,
  useToggle,
} from "@pushpress/shared-components";

export interface RecursionData {
  endDate?: string;
  occurrences?: number;
}

type Type = "never" | "on" | "after";

interface RecursionForm extends RecursionData {
  type: Type;
}

interface RecursionButtonProps {
  data?: RecursionData;
  onChange: (data: RecursionData) => void;
}

function RecursionButton(props: RecursionButtonProps) {
  const { data, onChange } = props;

  const [visible, visibleActions] = useToggle();

  const handleChange = (data: RecursionData) => {
    onChange(data);
    visibleActions.off();
  };

  const type = getType(data);

  let text = "Does not end";
  if (type === "after") {
    text = `End series after ${data.occurrences} occurrences`;
  } else if (type === "on") {
    text = `End series on ${formatInTimeZone(
      new Date(data.endDate),
      "UTC",
      "MM/dd/yyyy",
    )}`;
  }

  return (
    <>
      <Button
        text={text}
        buttonType="text"
        type="button"
        onClick={visibleActions.on}
      />
      <Modal visible={visible} closeModal={visibleActions.off} disableClickAway>
        <RecursionModalContent
          data={data}
          onChange={handleChange}
          onClose={visibleActions.off}
        />
      </Modal>
    </>
  );
}

interface RecursionModalContentProps {
  data?: RecursionData;
  onChange: (data: RecursionData) => void;
  onClose: VoidFunction;
}

const RecursionModalContent = ({
  data,
  onChange,
  onClose,
}: RecursionModalContentProps) => {
  const { control, handleSubmit } = useForm<RecursionForm>({
    defaultValues: {
      type: getType(data),
      occurrences: data.occurrences,
      endDate: data.endDate,
    },
  });

  const [type] = useWatch({
    control,
    name: ["type"],
  });

  const save = (values: RecursionForm) => {
    const nextValues = {
      endDate: values.endDate,
      occurrences: values.occurrences,
    };
    if (type === "on") {
      nextValues.occurrences = undefined;
    } else if (type === "after") {
      nextValues.endDate = undefined;
    } else {
      nextValues.endDate = undefined;
      nextValues.occurrences = undefined;
    }
    onChange(nextValues);
  };

  return (
    <div data-cy="class-ends-modal">
      <Modal.Card>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          paddingX={3}
          paddingY={3}
        >
          <Grid item>
            <Text variant="heading-4">End recurring series</Text>
          </Grid>
          <Grid item>
            <Grid container>
              <Grid item>
                <Button
                  text="Close"
                  buttonType="secondary"
                  size="medium"
                  onClick={onClose}
                />
              </Grid>
              <Grid item>
                <Button
                  data-cy="save-class-ends-button"
                  text="Save"
                  buttonType="primary"
                  size="medium"
                  type="button"
                  onClick={handleSubmit(save)}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Divider />
        <Modal.Content>
          <div data-cy="class-ends-type">
            <RadioController
              className="mt-4"
              data-cy="class-ends-type"
              options={[
                {
                  label: "Never",
                  value: "never",
                },
                {
                  label: "On a date",
                  value: "on",
                },
                {
                  label: "After",
                  value: "after",
                },
              ]}
              direction="vertical"
              control={control}
              name="type"
              label="Class ends type"
              hideLabel
              required
            />
          </div>
          {type === "on" && (
            <DateController
              name="endDate"
              label="On date"
              control={control}
              required
              autoComplete="off"
            />
          )}
          {type === "after" && (
            <NumberController
              name="occurrences"
              label="Occurrences"
              control={control}
              required
              autoComplete="off"
            />
          )}
        </Modal.Content>
      </Modal.Card>
    </div>
  );
};

function getType(data: RecursionData): Type {
  const { occurrences, endDate } = data;
  if (!occurrences && !endDate) {
    return "never";
  }
  if (occurrences > 0) {
    return "after";
  }
  return "on";
}

export default RecursionButton;
